import React, { useCallback } from 'react';

import { IReorderEntry } from 'features/menu/hooks/types';
import { useReorderEntries } from 'features/menu/hooks/use-reorder-entries';
import { RecentOrderCard } from 'features/menu/pages/recent-items-page/recent-order-card-list/recent-order-card-list-item';
import { useStoreContext } from 'features/store';
import { StyleSheet } from 'react-native';

import { IHistoricalOrder } from '@rbi-ctg/menu';
import ActionButton, { ActionButtonSizes } from 'components/action-button';
import { FavoriteOrderButton } from 'components/favorite-order-button';
import { Box, Typography } from 'components/ui';
import useOrderNumber from 'hooks/use-order-number';
import { logAddRecentItemToCartEvent } from 'state/amplitude';
import { t } from 'state/intl/translations';
import { useReorder } from 'state/order/hooks/use-reorder';
import { spacing } from 'styles/constants';
import { useCreateOrderAriaLabel } from 'utils/accessibility/aria';
import { routes } from 'utils/routing';

interface RecentOrderCardGroupProps {
  order: IHistoricalOrder;
}

const RecentOrderCardGroup: React.FC<React.PropsWithChildren<RecentOrderCardGroupProps>> = ({
  order,
}) => {
  const { store } = useStoreContext();
  const createAriaLabel = useCreateOrderAriaLabel();
  const { handleReorderClick } = useReorder(order, routes.menu);
  const reorderCartEntries = order?.cart.reorderCartEntries;
  const { reorderEntriesMap, checkingAvailability, hasSomeAvailableEntry } = useReorderEntries(
    reorderCartEntries
  );
  const orderNumber = useOrderNumber(order);
  const formattedDate = new Date(order.createdAt).toLocaleString(t('localeDateTime'), {
    month: 'long',
    day: 'numeric',
  });

  const handleReorder = useCallback(() => {
    if (order.cart?.reorderCartEntries) {
      for (const entry of order.cart.reorderCartEntries) {
        logAddRecentItemToCartEvent(entry?.name ?? '', store);
      }
      handleReorderClick();
    }
  }, [handleReorderClick, order.cart.reorderCartEntries, store]);

  if (!reorderCartEntries?.length) {
    return null;
  }

  return (
    <Box testID="recent-order-card-group" style={styles.cardGroup}>
      <Box style={styles.header}>
        <Box style={styles.headerDetails}>
          <Typography fontSize="sm" variant="headerFourLook">
            {formattedDate}
          </Typography>
          <FavoriteOrderButton order={order} />
          {orderNumber && (
            <Typography fontSize="sm" style={styles.orderNumber}>
              {t('order')} #{orderNumber}
            </Typography>
          )}
        </Box>
        <ActionButton
          testID="reorder-button"
          onPress={handleReorder}
          disabled={!hasSomeAvailableEntry}
          size={ActionButtonSizes.SMALL}
          aria-label={createAriaLabel({
            buttonType: 'reorder',
            order,
          })}
        >
          {t('reorder')}
        </ActionButton>
      </Box>
      {reorderCartEntries.map(cartEntry => {
        if (cartEntry.isDonation) {
          return null;
        }

        return (
          <RecentOrderCard
            key={cartEntry.lineId}
            cartEntry={cartEntry}
            mappedEntry={reorderEntriesMap[cartEntry.lineId] ?? ({} as IReorderEntry)}
            checkingAvailability={checkingAvailability}
          />
        );
      })}
    </Box>
  );
};

export default RecentOrderCardGroup;

const styles = StyleSheet.create({
  cardGroup: {
    paddingBottom: spacing.$3,
  },
  header: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingBottom: spacing.$4,
    paddingHorizontal: spacing.$3,
    paddingTop: spacing.$1,
  },
  headerDetails: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  orderNumber: {
    marginLeft: spacing.$2,
  },
});
